import { useMemo } from 'react';
import { Transaction } from '../types';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from 'recharts';
import { format, subMonths, isAfter, startOfMonth } from 'date-fns';

interface ChartsProps {
  transactions: Transaction[];
}

const COLORS = ['#3b82f6', '#10b981', '#f43f5e', '#f59e0b', '#8b5cf6', '#64748b', '#cbd5e1'];

export default function Charts({ transactions }: ChartsProps) {
  const { monthlyData, categoryData } = useMemo(() => {
    // 1. Prepare monthly Income vs Expense for the last 6 months
    const sixMonthsAgo = startOfMonth(subMonths(new Date(), 5));
    
    const monthlyMap = new Map<string, { income: number; expense: number }>();
    
    // Initialize last 6 months
    for (let i = 5; i >= 0; i--) {
      const dateStr = format(subMonths(new Date(), i), 'MMM yyyy');
      monthlyMap.set(dateStr, { income: 0, expense: 0 });
    }

    // 2. Prepare Expense Categories
    const catMap = new Map<string, number>();

    transactions.forEach(tx => {
      if (!tx.date) return;
      // A Balance Reconciliation correction isn't real income/spending --
      // see the same exclusion in Dashboard.tsx's monthly summary.
      if (tx.category === 'Balance Reconciliation') return;

      // Monthly aggregation
      if (isAfter(tx.date, sixMonthsAgo)) {
        const monthKey = format(tx.date, 'MMM yyyy');
        const current = monthlyMap.get(monthKey) || { income: 0, expense: 0 };
        
        if (tx.type === 'income') {
          current.income += tx.amount;
        } else if (tx.type === 'expense') {
          current.expense += tx.amount;
        }
        
        monthlyMap.set(monthKey, current);
      }

      // Category aggregation (only expenses)
      if (tx.type === 'expense') {
        const cat = tx.category || 'Uncategorized';
        catMap.set(cat, (catMap.get(cat) || 0) + tx.amount);
      }
    });

    const mData = Array.from(monthlyMap.entries()).map(([month, data]) => ({
      month,
      income: data.income,
      expense: data.expense
    }));

    const cData = Array.from(catMap.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6); // Top 6 categories

    return { monthlyData: mData, categoryData: cData };
  }, [transactions]);

  if (transactions.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-slate-400 bg-white rounded-xl border border-slate-200 shadow-sm">
        No transaction data available yet.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full">
      <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-col w-full">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-bold text-slate-800">Income vs Expenses (6 Mo)</h3>
        </div>
        <div className="flex-1 min-h-[250px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthlyData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8', fontWeight: 'bold' }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8', fontWeight: 'bold' }} tickFormatter={(val) => `$${val}`} />
              <Tooltip 
                cursor={{ fill: '#f8fafc' }}
                contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                formatter={(value: number) => [`$${value.toFixed(2)}`, undefined]}
              />
              <Legend iconType="circle" wrapperStyle={{ fontSize: '12px', fontWeight: 'bold', color: '#64748b' }} />
              <Bar dataKey="income" name="Income" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={40} />
              <Bar dataKey="expense" name="Expense" fill="#f43f5e" radius={[4, 4, 0, 0]} maxBarSize={40} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-col w-full">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-bold text-slate-800">Top Expense Categories</h3>
        </div>
        {categoryData.length > 0 ? (
          <div className="flex-1 min-h-[250px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={categoryData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                  stroke="none"
                >
                  {categoryData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value: number) => [`$${value.toFixed(2)}`, undefined]}
                  contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                />
                <Legend iconType="circle" layout="vertical" verticalAlign="middle" align="right" wrapperStyle={{ fontSize: '12px', fontWeight: 'bold', color: '#64748b' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
            No expenses to show.
          </div>
        )}
      </div>
    </div>
  );
}
